// Thin API client: same-origin, cookie session, Idempotency-Key on every mutation (05 §5).
import type { ApiError as ApiErrorBody } from '@syrova/contracts';
import { v7 as uuidv7 } from 'uuid';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown> | undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Thrown when the node itself cannot be reached (LAN down / node rebooting) — distinct from an API error. */
export class NodeUnreachableError extends Error {
  constructor() {
    super('node unreachable');
    this.name = 'NodeUnreachableError';
  }
}

export interface RequestOptions {
  /** reuse a key when retrying the same intent (e.g. after a timeout) */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

type FetchLike = typeof fetch;

export function createApi(fetchImpl: FetchLike = (...a) => fetch(...a), base = '') {
  async function call<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    opts: RequestOptions = {},
  ): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (method !== 'GET') headers['idempotency-key'] = opts.idempotencyKey ?? uuidv7();
    let res: Response;
    try {
      res = await fetchImpl(base + path, {
        method,
        headers,
        credentials: 'same-origin',
        body: body === undefined ? null : JSON.stringify(body),
        signal: opts.signal ?? null,
      });
    } catch {
      throw new NodeUnreachableError();
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const json: unknown = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const e = (json ?? {}) as Partial<ApiErrorBody>;
      throw new ApiError(
        res.status,
        e.code ?? `HTTP_${res.status}`,
        e.message ?? res.statusText,
        e.details,
      );
    }
    return json as T;
  }
  return {
    get: <T>(path: string, opts?: RequestOptions) => call<T>('GET', path, undefined, opts),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      call<T>('POST', path, body, opts),
    put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      call<T>('PUT', path, body, opts),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
      call<T>('PATCH', path, body, opts),
    delete: <T>(path: string, opts?: RequestOptions) => call<T>('DELETE', path, undefined, opts),
  };
}

export const api = createApi();
